import { useState } from 'react';
import type { ErklaerungStatus, ErklaerungApiError } from '@/types/erklaerung';
import { verifyBirthdate } from '@/api/erklaerung';

interface Props {
  token: string;
  erklaerung: ErklaerungStatus;
  onVerified: () => void;
  onLocked: (error: ErklaerungApiError) => void;
}

const MAX_ATTEMPTS = 3;

export function ErklaerungStep1Verify({ token, erklaerung, onVerified, onLocked }: Props) {
  const [birthdate, setBirthdate] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { studi } = erklaerung;
  const studinameDisplay = [studi.vorname, studi.nachname].filter(Boolean).join(' ') || 'Ihrem/r Mitarbeitenden';
  const studienbeginnDisplay = studi.studienbeginn
    ? new Date(studi.studienbeginn).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
    : studi.studienbeginn ?? '–';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!birthdate) return;
    setLoading(true);
    setError(null);
    try {
      await verifyBirthdate(token, birthdate);
      onVerified();
    } catch (err: unknown) {
      const apiErr = err as ErklaerungApiError;
      if (apiErr.code === 'locked') {
        onLocked(apiErr);
        return;
      }
      if (apiErr.code === 'wrong_birthdate') {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        setError(`Das Geburtsdatum ist nicht korrekt. Versuch ${newAttempts}/${MAX_ATTEMPTS}.`);
      } else {
        setError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-xl mx-auto mt-4">
      <h1 className="text-xl font-bold text-slate-900 mb-2">Sehr geehrte/r Zuzahler</h1>

      <div className="bg-slate-50 rounded-lg p-4 mb-6 text-sm text-slate-700 border border-slate-200">
        Sie möchten die Kostenübernahme für{' '}
        <span className="font-semibold">{studinameDisplay}</span>
        {studi.studienort && (
          <> an der FOM Hochschule, Studienort <span className="font-semibold">{studi.studienort}</span></>
        )}
        {studi.studienbeginn && (
          <>, ab Studienbeginn <span className="font-semibold">{studienbeginnDisplay}</span></>
        )}
        {' '}bestätigen.
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Geburtsdatum Ihres Mitarbeiters / Ihrer Mitarbeiterin
          </label>
          <p className="text-xs text-slate-500 mb-2">
            Bitte bestätigen Sie das Geburtsdatum, um Ihre Identität zu verifizieren.
          </p>
          <input
            type="date"
            value={birthdate}
            onChange={e => setBirthdate(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            required
            disabled={loading}
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !birthdate}
          className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg px-4 py-2.5 text-sm transition-colors"
        >
          {loading ? 'Wird überprüft…' : 'Identität bestätigen'}
        </button>
      </form>
    </div>
  );
}
